<template>
  <v-container v-if="visible" class="mt-4 mb-2" style="max-width: 1280px;">
    <v-card 
      id="intelligence-setup-banner"
      elevation="0" 
      class="intelligence-setup-banner"
    >
      <v-card-text class="pa-6">
        <div class="d-flex align-start">
          <!-- Icon -->
          <div class="mr-4" style="min-width: 48px;">
            <v-avatar size="48" class="banner-icon">
              <v-icon color="white" size="28">mdi-bullseye-arrow</v-icon>
            </v-avatar>
          </div>
          
          <!-- Content -->
          <div class="flex-grow-1">
            <div class="d-flex align-center mb-2">
              <h3 class="text-h6 font-weight-bold mr-3 banner-title">
                Complete Your Intelligence Setup
              </h3>
              <v-chip 
                x-small 
                color="primary" 
                dark 
                class="font-weight-bold progress-chip"
              >
                {{ safeSetupProgress }}% COMPLETE
              </v-chip>
            </div>
            
            <p class="text-body-2 mb-3 banner-description">
              Complete your intelligence setup to unlock powerful market insights. 
              Setting up all clusters helps you get the most value from the platform.
            </p>
            
            <!-- Progress Bar -->
            <div class="mb-3">
              <v-progress-linear
                :value="safeSetupProgress"
                color="primary"
                height="8"
                rounded
                class="progress-bar"
              ></v-progress-linear>
              <div class="text-caption mt-1 progress-text">
                {{ safeConfiguredClustersCount }} of 4 clusters configured
              </div>
            </div>
            
            <!-- Cluster Action Buttons -->
            <div class="d-flex flex-wrap cluster-buttons-container mb-4">
              <v-btn
                v-for="button in safeClusterButtons"
                :key="button.id"
                :color="button.color"
                :outlined="button.status !== 'complete'"
                :dark="button.status === 'complete'"
                small
                class="text-capitalize cluster-button"
                @click="$emit('cluster-click', button)"
              >
                <v-icon v-if="button.icon" left small>{{ button.icon }}</v-icon>
                {{ button.label }}
              </v-btn>
            </div>
            
            <!-- Action Buttons -->
            <div class="d-flex justify-end action-buttons-container">
              <v-btn
                outlined
                color="grey"
                small
                class="action-button"
                @click="$emit('dismiss')"
              >
                Dismiss
              </v-btn>
              <v-btn
                color="primary"
                dark
                small
                class="action-button"
                @click="$emit('continue-setup')"
              >
                Continue Setup
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: 'IntelligenceSetupBanner',
  
  props: {
    visible: {
      type: Boolean,
      required: true,
      default: false
    },
    setupProgress: {
      type: Number,
      required: true,
      default: 0,
      validator: (value) => {
        if (value === null || value === undefined) return false;
        const num = Number(value);
        return !isNaN(num) && num >= 0 && num <= 100;
      }
    },
    configuredClustersCount: {
      type: Number,
      required: true,
      default: 0
    },
    clusterButtons: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  
  computed: {
    safeSetupProgress() {
      const progress = Number(this.setupProgress);
      return isNaN(progress) ? 0 : Math.max(0, Math.min(100, progress));
    },
    
    safeConfiguredClustersCount() {
      const count = Number(this.configuredClustersCount);
      return isNaN(count) ? 0 : Math.max(0, count);
    },
    
    safeClusterButtons() {
      return Array.isArray(this.clusterButtons) ? this.clusterButtons : [];
    }
  }
};
</script>

<style scoped>
/* Surface colors: @/assets/css/intelligence-setup-surface.css */

.banner-icon {
  background-color: #ff9800;
  border-radius: 50%;
}

.progress-chip {
  height: 22px;
  font-size: 11px;
}

.cluster-buttons-container {
  gap: 8px;
}

.cluster-button {
  text-transform: none;
  font-weight: 500;
  font-size: 13px;
}

.action-buttons-container {
  gap: 8px;
}

.action-button {
  text-transform: none;
  font-size: 13px;
}
</style>
